import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { isErrorEventSeverity, SEVERITY_SELECT_OPTIONS, SeverityBadge } from './badges';
import { ErrorEventSeverity, SeverityGroup } from '../../../services/api';

/** Sentinel für „keine Default-Severity" — Radix-Select verträgt keinen leeren Wert. */
const NO_SEVERITY = '__none__';

/** Formularwerte des Gruppen-Dialogs. */
export interface SeverityGroupFormValues {
  name: string;
  /** `null` löscht eine vorhandene Beschreibung. */
  description: string | null;
  /** `null` = keine globale Gruppen-Regel (Mitglieder fallen auf den Rückfallwert). */
  globalSeverity: ErrorEventSeverity | null;
}

export interface SeverityGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` öffnet den Dialog im Anlege-Modus. */
  group: SeverityGroup | null;
  submitting: boolean;
  onSubmit: (values: SeverityGroupFormValues) => Promise<void>;
}

/**
 * Anlegen und Bearbeiten einer Fehler-Gruppe.
 *
 * Die Default-Severity wird hier mitgepflegt, ist serverseitig aber eine eigene
 * globale Gruppen-Regel — der aufrufende Tab setzt sie in einem zweiten Schritt.
 */
export function SeverityGroupDialog({
  open,
  onOpenChange,
  group,
  submitting,
  onSubmit,
}: SeverityGroupDialogProps) {
  const isEdit = group !== null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<string>(NO_SEVERITY);

  // Beim Öffnen aus der aktuellen Gruppe befüllen; im Anlege-Modus leeren.
  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setSeverity(group?.globalSeverity ?? NO_SEVERITY);
  }, [open, group]);

  const handleSubmit = async () => {
    await onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      globalSeverity: isErrorEventSeverity(severity) ? severity : null,
    });
  };

  const isValid = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Gruppe bearbeiten' : 'Gruppe anlegen'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Gruppen bündeln Identifier, die betrieblich gleich dringend sind. Die Default-Severity gilt für alle
            Identifier der Gruppe, die keinen eigenen Wert haben.
          </p>

          <div className="space-y-1">
            <Label htmlFor="severity-group-name">Name</Label>
            <Input
              id="severity-group-name"
              value={name}
              placeholder="z.B. Hooks & Nachlauf"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="severity-group-description">Beschreibung</Label>
            <Textarea
              id="severity-group-description"
              rows={2}
              value={description}
              placeholder="Wofür steht die Gruppe betrieblich?"
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Erscheint in der Gruppenliste und hilft beim Zuordnen neuer Identifier.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="severity-group-severity">Default-Severity (global)</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger id="severity-group-severity">
                <SelectValue placeholder="Keine" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SEVERITY}>Keine — Rückfallwert verwenden</SelectItem>
                {SEVERITY_SELECT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="flex items-center gap-2">
                      <SeverityBadge severity={option.value} />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Greift auf Stufe 4 der Auflösung — nach kundenspezifischen Regeln und nach einer Identifier-eigenen
              Severity.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {isEdit ? 'Änderungen speichern' : 'Gruppe anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
